/**
 * @file ReceiptDraftForm.tsx
 * @description Создание черновика приёмки: склад + № накладной поставщика
 * (вынесено из страницы приёмки, phase19.13 этап 0).
 *
 * phase19.31 (зона «Склад», минимум): заголовок «Новая приёмка» с «назад» в
 * верхней панели, «Создать черновик» — главное действие там же (на телефоне —
 * в общей нижней полосе); страница без белой карточки, поля до 720px.
 */

import { useEffect, useState } from "react";

import { Button } from "../ui/button";
import { CabinetPageHeader } from "../ui/CabinetPageHeader";
import { Input } from "../ui/input";
import { SelectField } from "../ui/SelectField";
import { useNotify } from "../ui/NotifyProvider";
import { WmsFieldLabel } from "./WmsFieldLabel";
import { ApiError } from "../../lib/api-client";
import {
  receiptsApi,
  warehousesApi,
  type GoodsReceiptDto,
  type WarehouseDto,
} from "../../lib/api-wms";

interface Props {
  onCreated: (receipt: GoodsReceiptDto) => void;
  /** Реестр приёмок — «назад». */
  backTo: string;
}

export function ReceiptDraftForm({ onCreated, backTo }: Props) {
  const notify = useNotify();
  const [warehouses, setWarehouses] = useState<WarehouseDto[]>([]);
  const [warehouseId, setWarehouseId] = useState("");
  const [vendorDocNumber, setVendorDocNumber] = useState("");
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    void warehousesApi.list().then((data) => {
      setWarehouses(data);
      const def = data.find((w) => w.isDefault) ?? data[0];
      if (def) setWarehouseId(def.id);
    });
  }, []);

  async function handleCreate() {
    if (!warehouseId) return;
    setCreating(true);
    try {
      const created = await receiptsApi.create({
        warehouseId,
        vendorDocNumber: vendorDocNumber.trim() || undefined,
      });
      notify.success("Черновик создан");
      onCreated(created);
    } catch (err) {
      notify.error(err instanceof ApiError ? err.message : "Не удалось");
    } finally {
      setCreating(false);
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <CabinetPageHeader
        title="Новая приёмка"
        subtitle="Черновик остаток не двигает: товар встанет на склад после проведения"
        back={{ to: backTo, label: "К приёмкам" }}
        action={
          <Button variant="brand" onClick={() => void handleCreate()} disabled={!warehouseId || creating} loading={creating}>
            Создать черновик
          </Button>
        }
      />

      <div className="grid max-w-[45rem] grid-cols-1 gap-3 sm:grid-cols-2">
        <WmsFieldLabel label="Склад">
          <SelectField
            value={warehouseId}
            onChange={setWarehouseId}
            options={warehouses.map((w) => ({ value: w.id, label: w.name }))}
            placeholder="— склад —"
            ariaLabel="Склад приёмки"
          />
        </WmsFieldLabel>

        <WmsFieldLabel label="№ накладной поставщика">
          <Input
            placeholder="Не обязательно"
            value={vendorDocNumber}
            onChange={(e) => setVendorDocNumber(e.target.value)}
          />
        </WmsFieldLabel>
      </div>
    </div>
  );
}
